import { useState, useEffect, useMemo } from "react";
import { Check, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ModalShell } from "@/components/ui/modal-shell";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { bookingsApi, type InsurancePackageWithPricing } from "@/lib/bookings-api";
import { getCurrencySymbol } from "@workspace/api-client-react";

interface InsuranceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  carId: number;
  rentalType: string;
  selectedPackageId: number | null;
  onSelectPackage: (packageId: number) => void;
  currency: string;
}

export function availableInsurancePackages(
  packages: InsurancePackageWithPricing[],
): InsurancePackageWithPricing[] {
  return packages.filter((pkg) => !pkg.blocked);
}

export function resolveAvailableInsuranceSelection(
  packages: InsurancePackageWithPricing[],
  selectedPackageId: number | null,
): number | null {
  return selectedPackageId != null && packages.some((pkg) => pkg.id === selectedPackageId)
    ? selectedPackageId
    : packages[0]?.id ?? null;
}

export function InsuranceModal({
  open,
  onOpenChange,
  carId,
  rentalType,
  selectedPackageId,
  onSelectPackage,
  currency,
}: InsuranceModalProps) {
  const { t } = useTranslation();
  const [packages, setPackages] = useState<InsurancePackageWithPricing[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [requestVersion, setRequestVersion] = useState(0);
  const [localSelected, setLocalSelected] = useState<number | null>(selectedPackageId);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPackages([]);
    setLocalSelected(selectedPackageId);
    setLoadFailed(false);
    setLoading(true);
    bookingsApi
      .getInsurancePackagesWithPricing(carId, rentalType)
      .then((res) => {
        if (!cancelled) {
          const available = availableInsurancePackages(res.packages || []);
          setPackages(available);
          setLocalSelected((current) => resolveAvailableInsuranceSelection(available, current));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPackages([]);
          setLoadFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, carId, rentalType, selectedPackageId, requestVersion]);

  const packageIds = useMemo(() => new Set(packages.map((pkg) => pkg.id)), [packages]);

  const handlePick = (pkgId: number) => {
    if (!packageIds.has(pkgId)) return;
    setLocalSelected(pkgId);
  };

  const handleConfirm = () => {
    if (localSelected != null && packageIds.has(localSelected)) {
      onSelectPackage(localSelected);
    }
    onOpenChange(false);
  };

  const handleCancel = () => {
    setLocalSelected(selectedPackageId);
    onOpenChange(false);
  };

  const sym = getCurrencySymbol(currency);

  const featureRows = [
    { label: t("insurance.feature_owner_liability"), key: "owner_liability_coverage" as const },
    { label: t("insurance.feature_renter_liability"), key: "renter_liability_coverage" as const },
    { label: t("insurance.feature_full_comp"), key: "full_comp" as const },
    { label: t("insurance.feature_customer_support"), key: "customer_support" as const },
  ];

  return (
    <ModalShell
      open={open}
      onClose={() => onOpenChange(false)}
      title={t("insurance.modal_title")}
      description={t("insurance.modal_description")}
      titleAlign="center"
      size="2xl"
      data-testid="insurance-modal"
      footer={
        !loading && !loadFailed && packages.length > 0 ? (
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="cursor-pointer rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={localSelected == null || !packageIds.has(localSelected)}
              className="rounded-lg bg-foreground px-5 py-2 text-sm font-semibold text-background hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("insurance.confirm_selection")}
            </button>
          </div>
        ) : null
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size="md" />
        </div>
      ) : loadFailed ? (
        <div className="flex flex-col items-center justify-center gap-4 py-10 text-center">
          <Shield className="h-5 w-5 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t("insurance.load_error")}</p>
          <button
            type="button"
            data-testid="insurance-packages-retry"
            onClick={() => setRequestVersion((version) => version + 1)}
            className="cursor-pointer rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
          >
            {t("common.try_again")}
          </button>
        </div>
      ) : packages.length === 0 ? (
        <div className="flex items-center gap-3 py-8">
          <Shield className="h-5 w-5 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t("insurance.no_packages_available")}</p>
        </div>
      ) : (
        <div>
          <p className="mb-3 text-center text-xs text-muted-foreground">
            {t("insurance.coverage_required_hint")}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  <th className="py-3 pr-4 text-left text-xs text-muted-foreground font-normal align-bottom">
                    {t("insurance.plans_tagline")}
                  </th>
                  {packages.map((pkg) => {
                    return (
                      <th
                        key={pkg.id}
                        className={cn(
                          "px-4 pt-5 pb-4 text-center min-w-[140px] transition-[border-color,background-color] align-bottom group/col",
                          "cursor-pointer",
                          localSelected === pkg.id
                            ? "bg-brand/5 border-x-2 border-t-2 border-brand rounded-t-lg"
                            : "border-x-2 border-t-2 border-transparent hover:border-brand/40 hover:bg-muted/40 rounded-t-lg",
                        )}
                        onClick={() => handlePick(pkg.id)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <span
                            className={cn(
                              "flex h-4 w-4 items-center justify-center rounded-full border-2 transition-colors shrink-0",
                              localSelected === pkg.id
                                  ? "border-brand bg-brand"
                                  : "border-muted-foreground/40 group-hover/col:border-brand/70",
                            )}
                          >
                            {localSelected === pkg.id && (
                              <span className="h-1.5 w-1.5 rounded-full bg-white" />
                            )}
                          </span>
                          <span className="font-bold text-base text-foreground">
                            {pkg.name}
                          </span>
                        </div>
                        <div className="mt-1.5 font-bold text-base text-foreground">
                          {t("insurance.daily_rate", { amount: `${sym}${pkg.dailyRate.toFixed(2)}` })}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {featureRows.map((row) => (
                  <tr key={row.label}>
                    <td className="py-3 pr-4 text-left text-muted-foreground border-t border-border">{row.label}</td>
                    {packages.map((pkg) => {
                      const isBool = row.key === "full_comp" || row.key === "customer_support";
                      const boolVal = isBool ? (pkg[row.key] ?? true) : false;
                      const textVal = !isBool
                        ? pkg[row.key as "owner_liability_coverage" | "renter_liability_coverage"]
                        : "";
                      const isSel = localSelected === pkg.id;
                      return (
                        <td
                          key={pkg.id}
                          onClick={() => handlePick(pkg.id)}
                          className={cn(
                            "px-4 py-3 text-center border-t cursor-pointer transition-colors",
                            isSel
                              ? "bg-brand/5 border-l-2 border-r-2 border-t-border border-l-brand border-r-brand"
                              : "border-t-border border-l-2 border-r-2 border-l-transparent border-r-transparent hover:bg-muted/40",
                          )}
                        >
                          {isBool ? (
                            boolVal ? (
                              <div className="flex justify-center">
                                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-brand">
                                  <Check className="h-3 w-3 text-white" />
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )
                          ) : (
                            <span>{textVal || "—"}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}

                <tr>
                  <td className="py-2 pr-4 text-left font-bold text-foreground text-xs uppercase tracking-wider border-t-2 border-border">
                    {t("insurance.deductibles")}
                  </td>
                  {packages.map((pkg) => {
                    const isSel = localSelected === pkg.id;
                    return (
                      <td
                        key={pkg.id}
                        onClick={() => handlePick(pkg.id)}
                        className={cn(
                          "px-4 py-2 border-t-2 border-t-border cursor-pointer transition-colors",
                          isSel
                            ? "bg-brand/5 border-l-2 border-r-2 border-l-brand border-r-brand"
                            : "border-l-2 border-r-2 border-l-transparent border-r-transparent hover:bg-muted/40",
                        )}
                      />
                    );
                  })}
                </tr>
                <tr>
                  <td className="py-3 pr-4 text-left text-muted-foreground border-t border-border">{t("insurance.physical")}</td>
                  {packages.map((pkg) => {
                    const isSel = localSelected === pkg.id;
                    return (
                      <td
                        key={pkg.id}
                        onClick={() => handlePick(pkg.id)}
                        className={cn(
                          "px-4 py-3 text-center tabular-nums border-t border-t-border cursor-pointer transition-colors",
                          isSel
                            ? "bg-brand/5 border-l-2 border-r-2 border-l-brand border-r-brand"
                            : "border-l-2 border-r-2 border-l-transparent border-r-transparent hover:bg-muted/40",
                        )}
                      >
                        {pkg.physical_deductible || "—"}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className="py-3 pr-4 text-left text-muted-foreground border-t border-border">{t("insurance.liability")}</td>
                  {packages.map((pkg) => {
                    const isSel = localSelected === pkg.id;
                    return (
                      <td
                        key={pkg.id}
                        onClick={() => handlePick(pkg.id)}
                        className={cn(
                          "px-4 py-3 text-center tabular-nums border-t border-t-border cursor-pointer transition-colors",
                          isSel
                            ? "bg-brand/5 border-l-2 border-r-2 border-b-2 border-l-brand border-r-brand border-b-brand rounded-b-lg"
                            : "border-l-2 border-r-2 border-l-transparent border-r-transparent hover:bg-muted/40",
                        )}
                      >
                        {pkg.liability_deductible || "—"}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </ModalShell>
  );
}
